import type { LexicalEditor, Spread } from "lexical";

import {
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  LexicalNode,
  NodeKey,
  SerializedTextNode,
  TextNode,
} from "lexical";
import { createVariableNode } from "../plugins/VariablesPlugin/utils";

// =============================================================================
// Types
// =============================================================================

export type SerializedVariableNode = Spread<
  {
    variableKey: string;
    type: "variable";
    version: 1;
    isValid?: boolean;
  },
  SerializedTextNode
>;

// =============================================================================
// Shared utilities
// =============================================================================

const variableStyle = "background-color: rgba(24, 119, 232, 0.2)";
const invalidVariableStyle =
  "text-decoration-line: underline; text-decoration-style: wavy; text-decoration-color: red; color: red;";

/**
 * Factory to create DOM conversion functions for variable nodes.
 */
function createVariableConverter(
  factory: (key: string, isValid?: boolean) => LexicalNode
) {
  return function (domNode: HTMLElement): DOMConversionOutput | null {
    const textContent = domNode.textContent;
    const variableKey = domNode.getAttribute("data-lexical-variable");

    if (textContent === null) {
      return null;
    }

    const key = variableKey === "schools.name" ? variableKey : textContent;
    return { node: factory(key, true) };
  };
}

/**
 * Creates the importDOM configuration for variable nodes.
 */
function createVariableImportDOM(
  factory: (key: string, isValid?: boolean) => LexicalNode
): DOMConversionMap {
  return {
    span: (domNode: HTMLElement) => {
      if (!domNode.hasAttribute("data-lexical-variable")) {
        return null;
      }
      return {
        conversion: createVariableConverter(factory),
        priority: 1,
      };
    },
  };
}

// =============================================================================
// VariableNode - Single class with export mode from settings/config
// =============================================================================

/**
 * Variable node for mustache-style template variables ({{variableName}}).
 * Export behavior is controlled by the editor config (variableExportMode from SettingsContext
 * or headless options). Call setVariableExportEditor(editor) before conversions so the node
 * reads the mode from context; it is not serialized.
 */
export class VariableNode extends TextNode {
  protected __variable: string;
  protected __isValid: boolean;
  /** Transient: never serialized (exportJSON omits it). Fallback when no current export editor is set. */

  constructor(variableKey: string, isValid: boolean = true, key?: NodeKey) {
    super(`{{${variableKey}}}`, key);
    this.__variable = variableKey;
    this.__isValid = isValid;
  }

  static getType(): string {
    return "variable";
  }

  static clone(node: VariableNode): VariableNode {
    return new VariableNode(node.__variable, node.__isValid, node.__key);
  }

  static importDOM(): DOMConversionMap | null {
    return createVariableImportDOM(createVariableNode);
  }

  static importJSON(serializedNode: SerializedVariableNode): VariableNode {
    const node = createVariableNode(
      serializedNode.variableKey,
      serializedNode.isValid ?? true
    );
    node.setFormat(serializedNode.format);
    node.setDetail(serializedNode.detail);
    node.setMode(serializedNode.mode);
    node.setStyle(serializedNode.style);
    return node;
  }

  isValid(): boolean {
    return this.__isValid;
  }

  setValid(valid: boolean): this {
    const self = this.getWritable();
    self.__isValid = valid;
    return self;
  }

  getVariableKey(): string {
    return this.__variable;
  }

  createDOM(config: EditorConfig): HTMLElement {
    const dom = super.createDOM(config);
    dom.style.cssText = this.__isValid ? variableStyle : invalidVariableStyle;
    return dom;
  }

  updateDOM(prevNode: this, dom: HTMLElement, config: EditorConfig): boolean {
    const shouldRecreate = super.updateDOM(prevNode, dom, config);
    if (shouldRecreate) {
      return true;
    }
    if (prevNode.__isValid !== this.__isValid) {
      dom.style.cssText = this.__isValid ? variableStyle : invalidVariableStyle;
    }
    return false;
  }

  isSegmented(): boolean {
    return false;
  }

  isTextEntity(): boolean {
    return false;
  }

  isToken(): boolean {
    return true;
  }

  exportJSON(): SerializedVariableNode {
    return {
      ...super.exportJSON(),
      variableKey: this.__variable,
      type: "variable",
      version: 1,
      isValid: this.__isValid,
    };
  }

  private createVariableElement(): HTMLElement | null {
    // <ul>{{#schools}}<li>{{{name}}}</li>{{/schools}}</ul>
    if (this.__variable === "schools.name") {
      const element = document.createElement("ul");
      element.appendChild(document.createTextNode("{{#schools}}"));
      const li = document.createElement("li");
      li.textContent = "{{{name}}}";
      element.appendChild(li);
      element.appendChild(document.createTextNode("{{/schools}}"));
      return element;
    }
    // <a href="{{{applyUrl}}}">{{{applyUrl}}}</a>
    if (this.__variable === "applyUrl") {
      const element = document.createElement("a");
      element.setAttribute("href", "{{{applyUrl}}}");
      element.textContent = "{{{applyUrl}}}";
      return element;
    }
    // <a href="mailto:{{supportEmail}}">{{supportEmail}}</a>
    if (this.__variable === "supportEmail") {
      const element = document.createElement("a");
      element.setAttribute("href", "mailto:{{supportEmail}}");
      element.textContent = "{{supportEmail}}";
      return element;
    }
    return null;
  }

  exportDOM(editor: LexicalEditor): DOMExportOutput {
    return this.exportHTML(editor, true);
  }

  exportHTML(
    editor: LexicalEditor,
    expandedMustache: boolean
  ): DOMExportOutput {
    const element = document.createElement("span");
    element.setAttribute("data-lexical-variable", this.__variable);

    const variableElement = expandedMustache
      ? this.createVariableElement()
      : null;
    if (variableElement) {
      element.appendChild(variableElement);
    } else {
      element.textContent = this.__text;
    }
    return { element };
  }

  getPlainText(expandedMustache: boolean = true): string {
    if (expandedMustache) {
      if (this.__variable === "schools.name") {
        return "{{#schools}}\n* {{{name}}}\n{{/schools}}";
      }
      if (this.__variable === "applyUrl") {
        return "{{{applyUrl}}}";
      }
    }

    return this.getTextContent();
  }
}
